var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { translate } from '@utils/i18n';
import { Text } from '@ui';
import { Tag } from '@ui/atoms/Tag';
import { Accordion } from '@ui/molecules/Accordion';
import { useIpaInspectionAssessment } from '../../hooks/useIpaInspectionAssessment';
import { useIssueBucketCounts } from '../../hooks/useIssueBucketCounts';
import { useOverrideInspectionAssessment } from '../../hooks/useOverrideInspectionAssessment';
import { iimReviewKeys } from '../../hooks/queryKeys';
import { IIM_RUN_COMPLETED_EVENT } from '../../events';
import { TechnicianRowList } from '../TechnicianRow';
import IssueTypeBucket from '../IssueTypeBucket';
import AssessmentFeedback from '../AssessmentFeedback';
import OverridePopup from '../OverridePopup';
import TechnicianScorecardDrawer from '../TechnicianScorecardDrawer';
import LegacyAiSummaryBanner from './LegacyAiSummaryBanner';
import './AiSummaryBanner.scss';
const NS = 'features.iim_review.ai_summary_banner';
const ASSESSMENT_NS = 'features.iim_review.ipa.inspection_assessment';
const BUCKET_SEVERITY_ORDER = [
    'finish_inspection',
    'resolve_building_issues',
    'verify_inspection_data',
    'verify_code_requirements',
    'verify_inspection_activity',
    'check_company_rules'
];
/* eslint-disable @typescript-eslint/naming-convention -- IpaInspectionAssessmentRating values are snake_case */
const RATING_LABEL_DEFAULTS = {
    critical: 'Critical',
    high_risk: 'High Risk',
    medium_risk: 'Medium Risk',
    low_risk: 'Low Risk'
};
const RATING_TAG_VARIANT = {
    critical: 'red',
    high_risk: 'red',
    medium_risk: 'yellow',
    low_risk: 'green'
};
/* eslint-enable @typescript-eslint/naming-convention */
function ratingLabel(rating) {
    return translate(`rating_${rating}`, { namespace: NS, defaultValue: RATING_LABEL_DEFAULTS[rating] });
}
const RATING_OPTIONS = Object.keys(RATING_LABEL_DEFAULTS).map((value) => ({
    value,
    label: ratingLabel(value)
}));
function IpaSummaryBanner({ inspectionId, ipaScorecardEnabled = false, canOverride = false }) {
    var _a;
    const { data: issueCounts } = useIssueBucketCounts(inspectionId);
    const { bucketCounts, typeCounts, primaryTechnician, assetCountChangeDetail } = issueCounts !== null && issueCounts !== void 0 ? issueCounts : {};
    const { data: assessment, refetch } = useIpaInspectionAssessment(inspectionId);
    const queryClient = useQueryClient();
    const overrideMutation = useOverrideInspectionAssessment({ inspectionId, assessmentId: (_a = assessment === null || assessment === void 0 ? void 0 : assessment.id) !== null && _a !== void 0 ? _a : 0 });
    const [overrideState, setOverrideState] = useState(null);
    const [bucketTechnicianDrawerOpen, setBucketTechnicianDrawerOpen] = useState(false);
    useEffect(() => {
        const handler = (event) => {
            const { detail } = event;
            if (detail.inspectionId !== inspectionId)
                return;
            void refetch();
            void queryClient.invalidateQueries({ queryKey: iimReviewKeys.bucketCounts(inspectionId) });
        };
        document.addEventListener(IIM_RUN_COMPLETED_EVENT, handler);
        return () => document.removeEventListener(IIM_RUN_COMPLETED_EVENT, handler);
    }, [inspectionId, refetch, queryClient]);
    const handleOverrideOpen = useCallback(() => {
        if (!assessment)
            return;
        setOverrideState({ originalValue: assessment.rating, newValue: assessment.rating });
    }, [assessment]);
    const handleOverrideClose = useCallback(() => setOverrideState(null), []);
    const handleNewValueChange = useCallback((newValue) => {
        setOverrideState((prev) => (prev ? Object.assign(Object.assign({}, prev), { newValue }) : null));
    }, []);
    const handleOverrideSave = useCallback((reason) => __awaiter(this, void 0, void 0, function* () {
        if (!assessment || !overrideState)
            return;
        try {
            yield overrideMutation.mutateAsync({ rating: overrideState.newValue, reason });
            setOverrideState(null);
        }
        catch (_a) {
            // Keep the popup open so the user can retry.
            toast.error(translate('override_error', {
                namespace: ASSESSMENT_NS,
                defaultValue: 'Could not save the override. Please try again.'
            }));
        }
    }), [assessment, overrideState, overrideMutation]);
    const handleBucketTechnicianClick = useCallback(() => setBucketTechnicianDrawerOpen(true), []);
    const handleBucketTechnicianDrawerClose = useCallback(() => setBucketTechnicianDrawerOpen(false), []);
    const activeBuckets = BUCKET_SEVERITY_ORDER.filter((key) => { var _a; return ((_a = bucketCounts === null || bucketCounts === void 0 ? void 0 : bucketCounts[key]) !== null && _a !== void 0 ? _a : 0) > 0; });
    const totalCount = activeBuckets.reduce((sum, key) => { var _a; return sum + ((_a = bucketCounts === null || bucketCounts === void 0 ? void 0 : bucketCounts[key]) !== null && _a !== void 0 ? _a : 0); }, 0);
    const hasBuckets = activeBuckets.length > 0;
    const isInProgress = ipaScorecardEnabled && ((assessment === null || assessment === void 0 ? void 0 : assessment.status) === 'pending' || (assessment === null || assessment === void 0 ? void 0 : assessment.status) === 'processing');
    const isComplete = ipaScorecardEnabled && (assessment === null || assessment === void 0 ? void 0 : assessment.status) === 'complete';
    if (!hasBuckets && !isInProgress && !isComplete)
        return null;
    const title = (React.createElement(Text, { variant: "small", as: "span", className: "ai-summary-banner__title-text" }, translate('header_title', { namespace: NS, defaultValue: 'Inspection Priority Assessment' })));
    const actionsContent = isComplete && assessment && (React.createElement("span", { className: "ai-summary-banner__priority" },
        React.createElement(Text, { variant: "x-small", as: "span", className: "ai-summary-banner__priority-label" }, translate('overall_priority', { namespace: ASSESSMENT_NS, defaultValue: 'Overall Priority:' })),
        React.createElement(Tag, { label: ratingLabel(assessment.rating), variant: RATING_TAG_VARIANT[assessment.rating], colorStyle: "solid", readOnly: true }),
        canOverride && (React.createElement("button", { type: "button", className: "ai-summary-banner__edit-btn", "aria-label": translate('override_rating_aria_label', {
                namespace: ASSESSMENT_NS,
                defaultValue: 'Override rating'
            }), onClick: handleOverrideOpen },
            React.createElement("i", { className: "fa-light fa-pen-to-square", "aria-hidden": "true" })))));
    return (React.createElement(Accordion, { title: title, actionsContent: actionsContent, variant: "inline", defaultExpanded: true, className: "ai-summary-banner" },
        React.createElement("div", { className: "ai-summary-banner__body" },
            isInProgress && (React.createElement(Text, { variant: "body", as: "p" }, translate('processing', {
                namespace: ASSESSMENT_NS,
                defaultValue: 'Peer analysis is still processing.'
            }))),
            isComplete && (assessment === null || assessment === void 0 ? void 0 : assessment.summary) && (React.createElement(Text, { variant: "body", as: "p", className: "ai-summary-banner__summary" }, assessment.summary)),
            (hasBuckets || ipaScorecardEnabled) && (React.createElement("div", { className: "ai-summary-banner__buckets" },
                activeBuckets.map((bucketKey) => {
                    var _a;
                    return (React.createElement(IssueTypeBucket, { key: bucketKey, bucketKey: bucketKey, count: (_a = bucketCounts === null || bucketCounts === void 0 ? void 0 : bucketCounts[bucketKey]) !== null && _a !== void 0 ? _a : 0, totalCount: totalCount, typeCounts: typeCounts, technician: bucketKey === 'finish_inspection' ? primaryTechnician : undefined, onTechnicianClick: handleBucketTechnicianClick, assetCountChangeDetail: bucketKey === 'verify_inspection_data' ? assetCountChangeDetail : undefined }));
                }),
                ipaScorecardEnabled && React.createElement(TechnicianRowList, { inspectionId: inspectionId }))),
            isComplete && assessment && React.createElement(AssessmentFeedback, { assessmentId: assessment.id })),
        overrideState && assessment && (React.createElement(OverridePopup, { isOpen: true, onClose: handleOverrideClose, onSave: handleOverrideSave, fieldLabel: ratingLabel(overrideState.originalValue), originalValue: overrideState.originalValue, newValue: overrideState.newValue, options: RATING_OPTIONS, onNewValueChange: handleNewValueChange, busy: overrideMutation.isLoading })),
        primaryTechnician && (React.createElement(TechnicianScorecardDrawer, { technicianId: primaryTechnician.id, inspectionId: inspectionId, isOpen: bucketTechnicianDrawerOpen, onClose: handleBucketTechnicianDrawerClose }))));
}
// TEMP(tmp_hide_new_ai_summary): remove when IP-15683 ships. The six-bucket /
// scorecard UI is unreleased, so tenants without ia_ipa_subprocesses_enabled get
// the master written-summary banner. Branching here (not inside IpaSummaryBanner)
// also skips its issues/assessment fetches when the flag is off.
function AiSummaryBanner({ inspectionId, ipaScorecardEnabled = false, canOverride = false }) {
    if (!ipaScorecardEnabled)
        return React.createElement(LegacyAiSummaryBanner, { inspectionId: inspectionId });
    return React.createElement(IpaSummaryBanner, { inspectionId: inspectionId, ipaScorecardEnabled: true, canOverride: canOverride });
}
export default AiSummaryBanner;
